import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import accountsApi from '../../services/accountsApi';
import { ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_COLORS = {
  Posted: 'bg-green-100 text-green-700',
  Reversed: 'bg-purple-100 text-purple-700',
  Draft: 'bg-gray-100 text-gray-600',
};

const JournalEntryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEntry = async () => {
      try {
        setLoading(true);
        const response = await accountsApi.get(`/journal-entries/${id}`);
        setEntry(response.data.journal_entry);
      } catch (err) {
        toast.error('Failed to load journal entry');
      } finally {
        setLoading(false);
      }
    };
    fetchEntry();
  }, [id]);

  if (loading) return <Layout title="Journal Entry"><div className="text-center py-8 text-gray-500">Loading...</div></Layout>;
  if (!entry) return <Layout title="Journal Entry"><div className="text-center py-8 text-gray-500">Journal entry not found</div></Layout>;

  return (
    <Layout title={`Journal Entry ${entry.entry_no}`}>
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
        <ArrowLeft size={14} /> Back
      </button>

      <div className="bg-white rounded-xl shadow p-6 mb-4">
        <div className="flex items-center gap-3 mb-4">
          <h2 className="text-xl font-bold text-gray-800">{entry.entry_no}</h2>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[entry.status] || 'bg-gray-100 text-gray-600'}`}>{entry.status}</span>
        </div>
        <div className="grid grid-cols-4 gap-4 text-sm">
          <div><div className="text-gray-500 text-xs">Entry Date</div><div className="font-medium text-gray-800">{entry.entry_date?.slice(0, 10)}</div></div>
          <div><div className="text-gray-500 text-xs">Source</div><div className="font-medium text-gray-800">{entry.source_type}</div></div>
          <div>
            <div className="text-gray-500 text-xs">Voucher</div>
            <div className="font-medium text-gray-800">
              {entry.voucher_no ? (
                <Link to={`/vouchers/${entry.voucher_id}`} className="text-blue-700 hover:underline">{entry.voucher_no}</Link>
              ) : '—'}
            </div>
          </div>
          <div><div className="text-gray-500 text-xs">Voucher Type</div><div className="font-medium text-gray-800">{entry.voucher_type_name || '—'}</div></div>
        </div>
        {entry.narration && <p className="text-sm text-gray-600 mt-3">{entry.narration}</p>}
        {entry.reverses_entry_id && (
          <div className="mt-3 text-sm">
            This entry reverses <Link to={`/journal-entries/${entry.reverses_entry_id}`} className="text-blue-700 hover:underline">entry #{entry.reverses_entry_id}</Link>
          </div>
        )}
        {entry.reversed_by_entry_id && (
          <div className="mt-1 text-sm">
            Reversed by <Link to={`/journal-entries/${entry.reversed_by_entry_id}`} className="text-blue-700 hover:underline">entry #{entry.reversed_by_entry_id}</Link>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl shadow overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-700">Lines</h3>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="text-left px-4 py-2 text-gray-500 font-medium text-xs">Account</th>
              <th className="text-left px-4 py-2 text-gray-500 font-medium text-xs">Description</th>
              <th className="text-right px-4 py-2 text-gray-500 font-medium text-xs">Debit</th>
              <th className="text-right px-4 py-2 text-gray-500 font-medium text-xs">Credit</th>
            </tr>
          </thead>
          <tbody>
            {entry.lines.map(l => (
              <tr key={l.id} className="border-t border-gray-50">
                <td className="px-4 py-2 text-gray-700">{l.account_code} — {l.account_name}</td>
                <td className="px-4 py-2 text-gray-500">{l.description || '—'}</td>
                <td className="px-4 py-2 text-right text-gray-800">{Number(l.debit || 0) > 0 ? Number(l.debit).toFixed(2) : ''}</td>
                <td className="px-4 py-2 text-right text-gray-800">{Number(l.credit || 0) > 0 ? Number(l.credit).toFixed(2) : ''}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-gray-200 font-semibold">
              <td colSpan={2} className="px-4 py-2 text-right text-gray-600 text-xs">Total</td>
              <td className="px-4 py-2 text-right text-gray-800">{Number(entry.total_debit).toFixed(2)}</td>
              <td className="px-4 py-2 text-right text-gray-800">{Number(entry.total_credit).toFixed(2)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </Layout>
  );
};

export default JournalEntryDetail;
